import { ChangeEvent, FocusEvent, FC, useCallback, useEffect, useState } from "react";
import { Control, Controller, UseFormGetValues, UseFormSetValue } from "react-hook-form";
import { FilterValues } from "widgets/mobileIndex/lib/hooks/useMobilePlace";
import { FormContainer, FormInput, FormItem, FormLabel, FormRemove } from "./style";
import { useAppSelector } from "shared/lib/store";
import { selectSettings } from "entities/settings/model/selector";
import { generateDurationObjects, minutesToTime } from "shared/lib/time/timeHelper";
import Select, { ControlProps, CSSObjectWithLabel, GroupBase, SingleValue } from 'react-select';
import { Option } from "shared/types";
import { MAIN_COLOR } from "shared/consts";
import { useDebounceCallback } from "shared/lib/debounce";

interface FormProps {
  control: Control<FilterValues, any>;
  getValues: UseFormGetValues<FilterValues>;
  setValue: UseFormSetValue<FilterValues>;
  setTables: () => Promise<void>;
  minDuration?: number;
  loadFreeSlots:  () => Promise<void>;
}

export const Form: FC<FormProps> = ({
  control,
  getValues,
  setValue,
  setTables,
  minDuration = 60,
  loadFreeSlots,
}) => {
  
  const appSettings = useAppSelector(selectSettings);
  const [durationObjects, setDurationObjects] = useState<Option[]>(generateDurationObjects(minutesToTime(minDuration), appSettings?.max_duration || '12:00', appSettings?.duration_step || 15));

  const [ defaultValue, setDefaultValue ] = useState<any>(null)

  const setTablesDebounced = useDebounceCallback(()=>{
    setTables();
  }, 500)

  const onQuantityChange = useCallback(async (event?: ChangeEvent<HTMLInputElement>) => {
    let val;
    if (!event?.target) {
      val = appSettings?.default_participants_count || 2
    } else {
      val = event.target.value
    }

    setValue('quantity', val);
    await loadFreeSlots();
    setTablesDebounced();
  }, [setValue, setTablesDebounced, loadFreeSlots, appSettings?.default_participants_count])

  const onDurationChange = useCallback((value: SingleValue<Option>) => {
    let dur;
    if (value) {
      dur = value;
    } else {
      dur = defaultValue.duration
    }

    setValue('duration', dur)
  }, [setValue, defaultValue])

  const selectStyles = {
    container: (baseStyles: CSSObjectWithLabel) => {
      return {
        ...baseStyles,
        marginTop: 'auto'
      }
    },
    control: (baseStyles: CSSObjectWithLabel, state: ControlProps<Option, false, GroupBase<Option>>) => {
      return {
        ...baseStyles,
        border: 'none',
        borderRadius: state.menuIsOpen ? '12px 12px 0 0' : '12px',
        minHeight: '35px',
        boxShadow: 'none',
      }
    },
    clearIndicator: (baseStyles: CSSObjectWithLabel) => {
      return {
        ...baseStyles,
        color: MAIN_COLOR,
        padding: '4px',
        cursor: 'pointer',
      }
    },
    dropdownIndicator: (baseStyles: CSSObjectWithLabel) => {
      return {
        ...baseStyles,
        color: MAIN_COLOR,
        padding: '4px',
        paddingRight: '13px',
        cursor: 'pointer',
      }
    },
    menu: (baseStyles: CSSObjectWithLabel) => {
      return {
        ...baseStyles,
        marginTop: 0,
        border: 'none',
        borderRadius: '0 0 12px 12px',
        boxShadow: 'none'
      }
    },
    option: (baseStyles: CSSObjectWithLabel) => {
      return {
        ...baseStyles,
        padding: '4px 12px'
      }
    },
    indicatorSeparator: () => {
      return {
        display: 'none'
      }
    }
  }

  useEffect(()=>{
    if (!defaultValue) {
      setDefaultValue(getValues())
    }
  }, [getValues, setDefaultValue, defaultValue])

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    e.target.select();
  }

  useEffect(() => {
    setDurationObjects(generateDurationObjects(minutesToTime(minDuration), appSettings?.max_duration || '12:00', appSettings?.duration_step || 15))
  }, [minDuration, appSettings?.max_duration, appSettings?.duration_step])

  return (
    <FormContainer>
      <FormItem>
        <FormLabel>Кол-во гостей</FormLabel>
				<Controller
					name="quantity"
					control={control}
					render={({ field }) => (
						<FormInput 
              inputMode="numeric"
              name={field.name}
              value={field.value}
              onChange={onQuantityChange}
              onFocus={handleFocus}
            />
					)}
				/>
        { (appSettings?.default_participants_count || defaultValue.quantity) != getValues().quantity &&
          <FormRemove onClick={() => onQuantityChange()}>
            <svg height="20" width="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false" fill={MAIN_COLOR}><path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z"></path></svg>
          </FormRemove>
        }
      </FormItem>
      <FormItem>
        <FormLabel>Длительность</FormLabel>
				<Controller
					name="duration"
					control={control}
					render={({ field }) => (
            <>
              <Select
                name={field.name}
                options={durationObjects}
                isClearable
                defaultValue={field.value}
                onChange={onDurationChange}
                styles={selectStyles}
                placeholder='Выберите'
              />
            </>
					)}
				/>
      </FormItem>
    </FormContainer>
  )
}